// Chrome ring cursor that trails the pointer and swells over interactive targets (fine pointers only).
const INTERACTIVE = 'a, button, [role="button"], label, summary, select, [commandfor]';

class SCursor extends HTMLElement {
  #abort = null;
  #raf = 0;
  #x = 0;
  #y = 0;
  #rx = 0;
  #ry = 0;
  #dot = null;
  #ring = null;
  #reduce = null;

  connectedCallback() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.#dot = this.querySelector('[data-cursor-dot]');
    this.#ring = this.querySelector('[data-cursor-ring]');
    if (!this.#dot || !this.#ring) return;

    this.#reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.documentElement.classList.add('has-custom-cursor');

    window.addEventListener('pointermove', (event) => this.#onMove(event), { signal, passive: true });
    window.addEventListener('pointerdown', () => this.toggleAttribute('data-pressed', true), { signal });
    window.addEventListener('pointerup', () => this.removeAttribute('data-pressed'), { signal });
    document.documentElement.addEventListener('pointerleave', () => this.removeAttribute('data-visible'), { signal });
    window.addEventListener('blur', () => this.removeAttribute('data-visible'), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
    document.documentElement.classList.remove('has-custom-cursor');
  }

  #onMove(event) {
    if (event.pointerType !== 'mouse') return;
    this.#x = event.clientX;
    this.#y = event.clientY;

    if (!this.hasAttribute('data-visible')) {
      this.#rx = this.#x;
      this.#ry = this.#y;
      this.toggleAttribute('data-visible', true);
    }

    const target = event.target instanceof Element ? event.target.closest(INTERACTIVE) : null;
    this.toggleAttribute('data-hover', Boolean(target));

    if (this.#reduce.matches) {
      this.#rx = this.#x;
      this.#ry = this.#y;
    }
    this.#queue();
  }

  #queue() {
    if (!this.#raf) this.#raf = requestAnimationFrame(() => this.#tick());
  }

  #tick() {
    this.#raf = 0;
    this.#rx += (this.#x - this.#rx) * 0.22;
    this.#ry += (this.#y - this.#ry) * 0.22;

    // clientX/Y are physical viewport coordinates, so the transforms stay physical in RTL too.
    this.#dot.style.transform = `translate3d(${this.#x}px, ${this.#y}px, 0)`;
    this.#ring.style.transform = `translate3d(${this.#rx}px, ${this.#ry}px, 0)`;

    if (Math.abs(this.#x - this.#rx) > 0.1 || Math.abs(this.#y - this.#ry) > 0.1) this.#queue();
  }
}

if (!customElements.get('s-cursor')) customElements.define('s-cursor', SCursor);
